import { useEffect, useRef } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import LoadWithSpinner from 'src/components/ui/Loading/LoadWithSpinner';
import { getSiteContext } from 'src/state/contexts/site';
import { getPoints } from 'src/state/entities/points/actions';
import { getPointsData } from 'src/state/entities/points/selectors';
import EmptyState from './EmptyState';
import { usePointsWidgets } from './hooks/usePointsWidgets';
import PointsTabs from './PointsTabs';
import analytics from 'src/helpers/analytics';

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    backgroundColor: 'white',
    display: 'flex',
    padding: 16,
  },
  loaderContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorMessage: {
    fontSize: 20,
    margin: '24px 48px 0',
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      margin: '16px 24px',
    },
  },
}));

const PointsPageLoader = () => {
  const classes = useStyles();
  const pointsData = useSelector(getPointsData);
  const dispatch = useDispatch();
  const site = useSelector(getSiteContext);
  const { id: siteId } = site;

  const sectionsWidgets = usePointsWidgets(pointsData, site);
  const startedLoadingMS = useRef(Date.now());

  useEffect(() => {
    if (!pointsData.loading) {
      const startTime = startedLoadingMS.current;
      if (startTime !== undefined && startTime !== null) {
        const duration = Date.now() - startTime;

        if (!pointsData.error) {
          analytics.trackEvent('Points data loading', {
            duration: duration.toString(),
          });
        }
      }
    }
  }, [pointsData.loading, pointsData.error, startedLoadingMS]);

  useEffect(() => {
    if (siteId !== pointsData.siteId) {
      dispatch(getPoints.request(siteId, false));
    }
  }, [pointsData, dispatch, siteId]);

  const readPoints = () => {
    dispatch(getPoints.request(siteId, true));
  };

  if (pointsData.error) {
    return <EmptyState text={pointsData.errorMessage} tryAgain={readPoints} />;
  }

  if (pointsData.loading) {
    return (
      <div className={`${classes.container} ${classes.loaderContainer}`}>
        <LoadWithSpinner message="Loading... This may take a few seconds" />
      </div>
    );
  }

  if (isEmpty(pointsData?.points)) {
    return <EmptyState text="No points found for this site." tryAgain={readPoints} />;
  }

  return (
    <div className={`${classes.container} ${pointsData.loading && classes.loaderContainer}`}>
      <PointsTabs
        widgetSections={sectionsWidgets}
        site={site}
        readPoints={readPoints}
        lastUpdated={pointsData.lastUpdated}
      />
    </div>
  );
};

export default PointsPageLoader;
